import type { Job, Issue, CustomerCredit } from "./types";
import type { ChangeOrder } from "./changeOrderTypes";
import { getJobDisplayValue } from "./jobPricingSelectors";
import { migrationBypasses } from "./import/migrationVisibilityGuard";

export type PriorityLevel = "Normal" | "Attention Needed" | "Critical";

export interface JobPriority {
  level: PriorityLevel;
  reasons: string[];
  financialImpact: number;
}

export function getJobPriority(
  job: Job,
  openIssues: Issue[],
  credits: CustomerCredit[] = []
): JobPriority {
  const reasons: string[] = [];
  let financialImpact = 0;

  // Issues blocking completion
  const urgentIssues = openIssues.filter((i) => i.severity === "Urgent");
  if (urgentIssues.length > 0) {
    reasons.push(`${urgentIssues.length} urgent issue${urgentIssues.length > 1 ? "s" : ""}`);
    financialImpact += getJobDisplayValue(job) * 0.1;
  }

  // QA incomplete after job stage passed In Progress
  if (!job.qaSubmitted && ["QA Review", "Completed"].includes(job.currentStage)) {
    reasons.push("QA incomplete");
  }

  // Materials missing or delayed
  const orderingBypassed = migrationBypasses(job, "ordering");
  const permittingBypassed = migrationBypasses(job, "permitting");

  if (!orderingBypassed && job.materialReadiness === "Missing Items") {
    reasons.push("Materials missing");
    financialImpact += getJobDisplayValue(job) * 0.05;
  }
  if (!orderingBypassed && job.materialReadiness === "Not Ordered" && job.currentStage !== "Sale Closed" && job.currentStage !== "Review and Validation" && job.currentStage !== "Review Complete") {
    reasons.push("Materials not ready");
  }

  // Change orders pending
  const pendingCOs = (job.changeOrders ?? []).filter((co) => co.status === "Draft" || co.status === "Pending Review");
  if (pendingCOs.length > 0) {
    const coValue = pendingCOs.reduce((s, co) => s + Math.abs(co.changeAmount), 0);
    reasons.push(`${pendingCOs.length} pending change order${pendingCOs.length > 1 ? "s" : ""}`);
    financialImpact += coValue;
  }

  // Payment/deposit missing
  if (job.financials?.paymentStatus === "Overdue") {
    reasons.push("Payment overdue");
    financialImpact += job.financials.contractValue;
  } else if (job.financials?.paymentStatus === "Not Invoiced" && job.currentStage !== "Sale Closed") {
    reasons.push("Not invoiced");
  }

  // Credits issued
  const jobCredits = credits.filter((c) => c.jobId === job.id);
  if (jobCredits.length > 0) {
    const totalCredits = jobCredits.reduce((s, c) => s + c.creditAmount, 0);
    if (totalCredits > 500) {
      reasons.push(`$${totalCredits.toLocaleString()} in credits`);
      financialImpact += totalCredits;
    }
  }

  // Scheduled but not ready
  if (
    job.scheduledInstallDate &&
    job.currentStage !== "Completed" &&
    ((!orderingBypassed && (job.materialReadiness === "Not Ordered" || job.materialReadiness === "Missing Items")) ||
     (!permittingBypassed && job.permitRequired && job.permitStatus !== "Approved"))
  ) {
    reasons.push("Scheduled but not ready");
  }

  // Contractor rejected
  if (job.dispatchStatus === "Rejected") {
    reasons.push("Contractor rejected");
  }

  // Determine level
  let level: PriorityLevel = "Normal";
  if (reasons.length >= 3 || urgentIssues.length > 0 || job.financials?.paymentStatus === "Overdue" || job.dispatchStatus === "Rejected") {
    level = "Critical";
  } else if (reasons.length > 0) {
    level = "Attention Needed";
  }

  return { level, reasons, financialImpact };
}

export function getJobFinancialSummary(job: Job, credits: CustomerCredit[] = []) {
  const changeOrders = job.changeOrders ?? [];
  const signedCOs = changeOrders.filter((co) => co.status === "Signed");
  const totalCOValue = signedCOs.reduce((s, co) => s + co.changeAmount, 0);
  
  // Canonical: customer-facing contract value resolves through getJobDisplayValue().
  // Job.payoutAmount is contractor/sub payout only and must NEVER drive customer
  // revenue, contract value, or operator financial summaries.
  const currentContract = getJobDisplayValue(job);
  const originalContract = currentContract - totalCOValue;
  
  const jobCredits = credits.filter((c) => c.jobId === job.id);
  const totalCredits = jobCredits.reduce((s, c) => s + c.creditAmount, 0);

  return {
    originalContract,
    totalChangeOrderValue: totalCOValue,
    changeOrderCount: signedCOs.length,
    currentContract,
    totalCredits,
    creditCount: jobCredits.length,
    netValue: currentContract - totalCredits,
  };
}
